import {inject, Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, switchMap, throwError} from 'rxjs';
import {catchError} from "rxjs/operators";
import {AuthService} from "@app/_services/auth.service";
import {environment} from "@environments/environment";

@Injectable() export class HttpRequestInterceptor implements HttpInterceptor {
    private authService = inject(AuthService);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const authedRequest = request.clone({withCredentials: true});

        return next.handle(authedRequest).pipe(catchError(originalRequestError => {
            const isRefreshTokenUrl = request.url.startsWith(`${environment.apiBaseUrl}/v3/admin/refresh-token`);

            if ([401, 403].includes(originalRequestError.status)) {
                if (isRefreshTokenUrl) {
                    // logout if the auth error was from a refresh-token request to avoid infinite loops
                    this.authService.logout();
                    return throwError(() => originalRequestError);
                }

                if (this.authService.userValue != null) {
                    // This makes a fresh HttpClient call, so this will get picked up by the interceptor
                    return this.authService.refreshToken(this.authService.userValue.email!).pipe(
                        switchMap(() => {
                            /*
                                Since this next.handle() is the handler passed into intercept(), this request
                                bypasses this interceptor and goes straight to whatever is downstream.

                                Catch auth errors from the retry attempts and logout.
                            */
                            return next.handle(authedRequest).pipe(
                                catchError((retryError) => {
                                    if ([401, 403].includes(retryError.status)) {
                                        // retry resulted in auth error so logout
                                        this.authService.logout();
                                    }
                                    return throwError(() => retryError);
                                })
                            );
                        })
                    );
                }
            }

            // throw error by default so it can be handled by the caller
            return throwError(() => originalRequestError);
        }));
    }
}
